// A modal's two queries as one load: the subject it is about and a second one that names what it shows.
// Ready only when both have data, failed with the failed query's own message, missing on a 404 for the subject.
// Not a hook: it reads query results the caller already holds, so it can be tested without React.
import { pairFailure, pairLoad, type Loadable, type JobLoad } from "./jobForms";

/** What a query exposes to a paired modal. A React Query result fits. */
export interface PairQuery<T> extends Loadable {
  data: T | undefined;
  isFetching: boolean;
  refetch: () => unknown;
}

export interface PairedData<A, B> {
  subject: A | undefined;
  support: B | undefined;
  load: JobLoad;
  /** What a "failed" load says beside Retry. */
  failure: string;
  retrying: boolean;
  /** Refetches only the query that has no data. */
  retry: () => void;
}

export function pairedData<A, B>(subject: PairQuery<A>, support: PairQuery<B>, messages: { subject: string; support: string }): PairedData<A, B> {
  return {
    subject: subject.data,
    support: support.data,
    load: pairLoad(subject, support),
    failure: pairFailure(subject, messages.subject, messages.support),
    retrying: subject.isFetching || support.isFetching,
    retry: () => {
      if (subject.data === undefined) void subject.refetch();
      if (support.data === undefined) void support.refetch();
    },
  };
}
